/**
 * Cart Form Component
 * Handles cart updates, quantity changes, and item removal
 * @extends HTMLElement
 */
class AjaxCart extends HTMLElement {
    constructor() {
      super();

      this._isRequestInFlight = false;

      this._handleSubmit = (e) => {
        const submitter = e.submitter;
        const isCheckout = submitter?.name === 'checkout' || submitter?.id === 'checkout';
        if (isCheckout) {
          e.preventDefault();
          if (submitter instanceof HTMLButtonElement) {
            Utility.Animate.setLoading(submitter, true, { disable: true });
          }
          globalThis.location.assign(globalThis.routes?.checkout_url || '/checkout');
          return;
        }

        // Prevent default form submission - quantity updates are handled via AJAX
        e.preventDefault();
      };

      this._handlePageShow = (event) => {
        // Reset checkout/action loading UI after bfcache restore (iOS Back from checkout)
        this._isRequestInFlight = false;
        this._setCartFormDisabled(false);
        this.querySelectorAll('.btn.loading, [data-loading-active="true"]').forEach((button) => {
          Utility.Animate.setLoading(button, false);
        });
        this._actionButton = null;

        // Cart may have changed on checkout — re-fetch when restored from bfcache
        if (event.persisted) {
          this._refreshCartFromServer();
        }
      };

      this._handleCartUpdateEvent = ({ data }) => {
        this._updateCartUI(data.section, data.elementToFocus);
      };

      this._handleClickEvent = (e) => {
        const {target} = e;
        if (!target || !(target instanceof Element) || !this.cartForm?.contains(target) || this._isRequestInFlight) return;

        const removeBtn = target.hasAttribute('data-item-remove') ? target : target.closest?.('[data-item-remove]');
        if (removeBtn && !removeBtn.disabled) {
          this._handleCartItemAction(e, 0);
        }
      };

      this._handleInputEvent = (e) => {
        const {target} = e;
        if (!target || !(target instanceof Element) || !this.cartForm?.contains(target) || this._isRequestInFlight) return;

        const qtyInput = target.hasAttribute('data-qty-input') ? target : target.closest?.('[data-qty-input]');
        if (qtyInput instanceof HTMLInputElement && !qtyInput.disabled) {
          const value = qtyInput.value?.trim();
          if (value && !isNaN(value) && Number(value) >= 0) {
            this._handleCartItemAction(e, value);
          }
        }
      };
    }

    connectedCallback() {
      this.cartDrawer = Utility.DomSelector.$('#component-cart-drawer');
      this.cartForm = Utility.DomSelector.$('form[data-cart-form]', this);

      // Use event delegation for cart form interactions
      this.cartForm?.addEventListener('submit', this._handleSubmit);
      this.cartForm?.addEventListener('click', this._handleClickEvent);
      this.cartForm?.addEventListener('input', this._handleInputEvent);
      globalThis.addEventListener('pageshow', this._handlePageShow);

      // Initialize Shopify checkout and payment buttons
      this._initializeShopifyFeatures();

      this.unsubscribeCartUpdate = eventBus.subscribe(THEME_EVENTS.cartUpdate, this._handleCartUpdateEvent);

      this._openCartDrawerFromRedirect();
    }

    /**
     * Activate deferred cart drawer stylesheet and wait for it to apply
     */
    _loadCartDrawerStyles() {
      return new Promise((resolve) => {
        let link = document.querySelector('link[data-href*="component-cart-drawer"]');
        if (link?.dataset.href) {
          link.href = link.dataset.href;
          link.removeAttribute('data-href');
        } else {
          link = document.querySelector('link[href*="component-cart-drawer"]');
        }

        if (!link) {
          resolve();
          return;
        }

        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true });

        if (link.sheet) resolve();
      });
    }

    /**
     * Open cart drawer after redirect from /cart to home
     */
    _openCartDrawerFromRedirect() {
      try {
        if (sessionStorage.getItem('openCartDrawer') !== '1') return;
        sessionStorage.removeItem('openCartDrawer');
      } catch {
        return;
      }

      const openDrawer = () => {
        const drawer = this.cartDrawer || Utility.DomSelector.$('#component-cart-drawer');
        if (!drawer?.openDrawer) return;

        const trigger = Utility.DomSelector.$('[data-toggleDrawer="component-cart-drawer"]');
        drawer.openDrawer(trigger);
      };

      const whenDrawerReady = () => {
        if (customElements.get('custom-drawer')) {
          requestAnimationFrame(openDrawer);
        } else {
          customElements.whenDefined('custom-drawer').then(() => requestAnimationFrame(openDrawer));
        }
      };

      this._loadCartDrawerStyles().then(whenDrawerReady);
    }

    /**
     * Handle cart item action (remove or update quantity)
     * @param {Event} e - Event object
     * @param {number} quantity - Quantity value (0 for remove)
     */
    _handleCartItemAction(e, quantity) {
      e.preventDefault();
      const itemParent = e.target.closest('[data-cart-item]');
      const line = itemParent?.dataset?.line;
      if (!itemParent || !line) return;

      const actionButton = e.target.closest('[data-item-remove]');
      this._actionButton = actionButton || null;
      if (actionButton) Utility.Animate.setLoading(actionButton, true);

      // Store selector for focus restoration after DOM update (HTML will be replaced)
      this._focusSelector = quantity === 0 
        ? `[data-cart-item][data-line="${line}"]` 
        : `[data-cart-item][data-line="${line}"] [data-qty-input]`;
      this.updateItem(line, quantity);
    }

    /**
     * Set cart form disabled state
     * Adds/removes 'updating' class to disable user interactions via CSS
     * @param {boolean} disabled - Whether to disable the form
     */
    _setCartFormDisabled(disabled) {
      if (!this.cartForm) return;
      this.cartForm.classList.toggle('updating', disabled);
      this.cartForm.setAttribute('aria-busy', String(disabled));
    }

    /**
     * Initialize Shopify checkout and payment button features
     * Called after DOM updates to reinitialize Shopify features
     */
    _initializeShopifyFeatures() {
      globalThis?.Shopify?.Checkout?.configureExpressCheckout?.();
      globalThis?.Shopify?.PaymentButton?.init();
    }
  
    /**
     * Open cart drawer
     * @param {HTMLElement} elementTofocus - Button that initiates opening cart drawer after update. Used for focus upon drawer close.
     */
    initCartDrawer(elementTofocus = null) {
      if (this.cartDrawer) {
        this.cartDrawer.openDrawer(elementTofocus);
      } else {
        globalThis.location.href = globalThis.routes.cart_fetch_url || '/cart';
      }
    }

    /**
     * Re-fetch this cart section from the server (used after bfcache restore)
     */
    async _refreshCartFromServer() {
      const { sectionId } = this.dataset;
      if (!sectionId || !this.isConnected) return;

      try {
        const cartUrl = globalThis.routes?.cart_fetch_url || '/cart';
        const separator = cartUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${cartUrl}${separator}sections=${encodeURIComponent(sectionId)}`);
        if (!response.ok || !this.isConnected) return;

        const sections = await response.json();
        const sectionHTML = sections?.[sectionId];
        if (!sectionHTML) return;

        eventBus.publish(THEME_EVENTS.cartUpdate, {
          data: {
            action: 'refresh',
            item: null,
            section: sectionHTML
          }
        });
      } catch (error) {
        console.error('Failed to refresh cart after back navigation:', error);
      }
    }

    /**
     * Update cart UI from HTML
     * @param {string} section - HTML string or DOM element
     * @param {HTMLElement} openDrawerBtn - Button that initiates opening cart drawer after update. Used for focus upon drawer close.
     */
    _updateCartUI(section, openDrawerBtn = null) {
      if (!section) return;

      try {
        const cartHTML = typeof section === 'string' ? Utility.DOM.parse(section) : section;
        const sectionId = this.dataset.sectionId;

        // Ignore section HTML that belongs to a different cart instance (page vs drawer)
        if (
          !cartHTML.querySelector(`#cart-form-${sectionId}`) &&
          !cartHTML.querySelector(`#cart-form-container-${sectionId}`)
        ) {
          return;
        }

        const cartJSONEle = Utility.DOM.extract(cartHTML, '[data-cartScriptJSON]');
        if (cartJSONEle) {
          try {
            globalThis.themeData.cart = { ...JSON.parse(cartJSONEle.textContent) };
          } catch (parseError) {
            console.error('Failed to parse cart JSON:', parseError);
          }
        }

        const selectors = [
          'cart-form', 'cart-subtotal', 'cart-discount',
          'cart-total', 'cart-discount-codes', 'cart-item-count'
        ].map(id => `#${id}-${sectionId}`);

        Utility.DOM.updateSelectors(selectors, cartHTML);
        Utility.Cart.updateHeaderCount();

        const cartFormContainer = this.querySelector(`#cart-form-container-${sectionId}`);
        cartFormContainer?.classList.toggle('empty_cart', globalThis.themeData.cart.item_count <= 0);

        // Open drawer before Shopify payment button init so ATC feels instant
        if (this.cartDrawer && openDrawerBtn) this.initCartDrawer(openDrawerBtn);

        // Defer checkout button re-init — it can block the main thread
        const initShopifyFeatures = () => this._initializeShopifyFeatures();
        if (typeof globalThis.requestIdleCallback === 'function') {
          globalThis.requestIdleCallback(initShopifyFeatures, { timeout: 500 });
        } else {
          setTimeout(initShopifyFeatures, 0);
        }

        // Restore focus to the element that triggered the update
        if (this._focusSelector) {
          const isRemove = !this._focusSelector?.includes('[data-qty-input]');
          let element = Utility.DomSelector.$(this._focusSelector, this.cartForm);
          if (isRemove && !element) {
            const items = Utility.DomSelector.$$('[data-cart-item]', this.cartForm);
            element = items[0];
          }
          (isRemove ? Utility.DomSelector.$('[data-item-remove], [data-qty-input]', element) : element)?.focus();
          this._focusSelector = null;
        }
      } catch (error) {
        // If DOM update fails, remove loading classes from all cart items as fallback
        console.error('Error updating cart UI:', error);
        this._setCartFormDisabled(false);
        this._isRequestInFlight = false;
      }
    }

    /**
     * Update cart item quantity or remove item
     * @param {number} line - Line item index (1-based)
     * @param {number} quantity - Quantity value (0 for remove)
     * @param {object} properties - Properties to update (optional)
     */
    async updateItem(line, quantity, properties = null) {
      if (!this.isConnected) return;
      if (this._isRequestInFlight) return;

      const {sectionId} = this.dataset;
      if (!sectionId) {
        console.warn('Cart form missing sectionId data attribute');
        return;
      }

      const parsedLine = Number.parseInt(line, 10);
      const parsedQuantity = Number.parseInt(quantity, 10);
      if (isNaN(parsedLine) || isNaN(parsedQuantity) || parsedQuantity < 0) {
        console.warn('Invalid line or quantity:', { line, quantity });
        return;
      }

      const cartItem = Utility.DomSelector.$(`[data-cart-item][data-line="${line}"]`, this.cartForm);
      const cartChangeUrl = globalThis.routes?.cart_change_url || '/cart/change.js';

      this._isRequestInFlight = true;
      this._setCartFormDisabled(true);
      const body = JSON.stringify({
        line: parsedLine,
        quantity: parsedQuantity,
        ...(properties && { properties }),
        sections: sectionId
      });

      try {
        const dataJSON = await Utility.Fetch.json(cartChangeUrl, {
          ...Utility.Fetch.config(),
          body
        });

        const isRemove = parsedQuantity === 0;
        const itemData = isRemove ? dataJSON.items_removed : dataJSON.items_added;
        const cartSection = dataJSON.sections?.[sectionId];

        // Update cart data from response if available
        if (dataJSON.item_count !== undefined) {
          globalThis.themeData.cart.item_count = dataJSON.item_count;
        }
        const itemCount = globalThis.themeData.cart.item_count;

        const itemTitle = itemData?.[0]?.title || 'Item';
        const message = isRemove
          ? `${itemTitle} removed from cart. Cart now has ${itemCount} item${itemCount !== 1 ? 's' : ''}`
          : `${itemTitle} quantity updated to ${parsedQuantity}`;
        Utility.announceToScreenReader(message);

        // Publish event if section exists
        if (cartSection) {
          eventBus.publish(THEME_EVENTS.cartUpdate, {
            data: { 
              action: isRemove ? 'remove-item' : 'update-qty', 
              item: itemData, 
              section: cartSection
            }
          });
        }

      } catch (error) {
        if (!this.isConnected) return;

        if (error.status === 422) {
          const qtyInput = cartItem?.querySelector('input[data-qty-input]');
          const lineIndex = parsedLine - 1;
          const currentQty = globalThis.themeData?.cart?.items[lineIndex]?.quantity;
          if (qtyInput && currentQty !== undefined) qtyInput.value = currentQty;
        }

        const message = Utility.extractErrorMessage(error) || 'Failed to update cart';
        Utility.handleError(message, 'Cart Update');
        Utility.announceToScreenReader(message, 'assertive');
      } finally {
        this._isRequestInFlight = false;
        this._setCartFormDisabled(false);
        if (this._actionButton) {
          Utility.Animate.setLoading(this._actionButton, false);
          this._actionButton = null;
        }
      }
    }
  
    /**
     * Clean up event listeners and references
     */
    disconnectedCallback() {
      this.cartForm?.removeEventListener('submit', this._handleSubmit);
      this.cartForm?.removeEventListener('click', this._handleClickEvent);
      this.cartForm?.removeEventListener('input', this._handleInputEvent);
      globalThis.removeEventListener('pageshow', this._handlePageShow);
      this.unsubscribeCartUpdate?.();
      
      this.cartForm = null;
      this.cartDrawer = null;
    }
}
customElements.define("cart-form", AjaxCart);